import { ActionLog, useActionLogsQuery } from '@app/api/dieter/actionLog/useActionLogsQuery';
import { useMarkActionLogsAsSeenMutation } from '@app/api/dieter/actionLog/useMarkActionLogsAsSeenMutation';
import { useQuestionnairesQuery } from '@app/api/dieter/questionnaire/useQuestionnairesQuery';
import { NavigationSection, NavigationSubsection, Topics } from '@app/context/constants';
import { useTasks } from '@app/context/TaskContext';
import { useNavigation } from '@app/hooks/useNavigation';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { Application, useApplicationTitleLocalesQuery } from '@dieterApi/application/useApplicationTableQuery';
import type { Task } from '@dieterApi/task/useTaskQuery';
import { NotificationsNone as NotificationsNoneIcon } from '@mui/icons-material';
import { Badge, Box, Chip, Divider, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { t } from 'i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const ACTION_TASK_COMPLETE = 'task.complete';
export const ACTION_TASK_REOPEN = 'task.reopen';
export const ACTION_DPO_TASK_WITHDRAW_APPROVAL = 'dpo_task.withdraw_approval';
export const ACTION_CHAT_MESSAGE_ADD = 'chat.message_add';
export const ACTION_DPO_TASK_APPROVE = 'dpo_task.approve';
// keep in sync with dieter-server src/utils/actionLogTypes.ts
export const ACTION_RIGHTS_DEADLINE_WARNING = 'rights_request.deadline_warning';
export const ACTION_INCIDENT_DEADLINE_WARNING = 'incident.deadline_warning';
export const ACTION_RIGHTS_REQUEST_CREATED = 'rights_request.created';
export const ACTION_INCIDENT_CREATED = 'incident.created';

export const NotificationTypes = {
  info: 'info',
  warning: 'warning',
  error: 'error',
  success: 'success',
};

export function mapActionToNotificationTitle(action: string): string {
  switch (action) {
    case ACTION_CHAT_MESSAGE_ADD:
      return t('dpo.notifications.chat_message_add', 'Neue Chat-Nachricht');
    case ACTION_TASK_COMPLETE:
      return t('dpo.notifications.task_complete', 'Aufgabe abgeschlossen');
    case ACTION_TASK_REOPEN:
      return t('dpo.notifications.task_reopen', 'Aufgabe eröffnet');
    case ACTION_DPO_TASK_WITHDRAW_APPROVAL:
      return t('dpo.notifications.dpo_task_withdraw_approval', 'Abnahme zurückgezogen');
    case ACTION_DPO_TASK_APPROVE:
      return t('dpo.notifications.dpo_task_approve', 'Abnahme erteilt');
    case ACTION_RIGHTS_DEADLINE_WARNING:
      return t('dpo.notifications.rights_deadline_warning', 'Frist einer Betroffenenanfrage läuft ab');
    case ACTION_INCIDENT_DEADLINE_WARNING:
      return t('dpo.notifications.incident_deadline_warning', 'Meldefrist eines Datenschutzvorfalls läuft ab');
    case ACTION_RIGHTS_REQUEST_CREATED:
      return t('dpo.notifications.rights_request_created', 'Neue Betroffenenanfrage erfasst');
    case ACTION_INCIDENT_CREATED:
      return t('dpo.notifications.incident_created', 'Neuer Datenschutzvorfall erfasst');
    default:
      return `${action.split('.')[1]?.toUpperCase()} - ${action.split('.')[0]}`;
  }
}

function mapActionToNotificationType(action: string): string {
  if (action === ACTION_TASK_COMPLETE) {
    return NotificationTypes.success;
  }
  if (action === ACTION_TASK_REOPEN) {
    return NotificationTypes.info;
  }
  if (action === ACTION_CHAT_MESSAGE_ADD) {
    return NotificationTypes.info;
  }
  if (action === ACTION_DPO_TASK_WITHDRAW_APPROVAL) {
    return NotificationTypes.warning;
  }
  if (action === ACTION_DPO_TASK_APPROVE) {
    return NotificationTypes.success;
  }
  if (action === ACTION_RIGHTS_DEADLINE_WARNING || action === ACTION_INCIDENT_DEADLINE_WARNING) {
    return NotificationTypes.warning;
  }

  return NotificationTypes.info; // default type
}

// extracts the application ID from the resource name
function extractApplicationId(resourceString: string): string {
  if (!resourceString) return 'default';

  const applicationIndex = resourceString.indexOf('application-');

  if (applicationIndex === -1) {
    return 'default';
  }

  // Get everything after "application-"
  const afterApplication = resourceString.substring(applicationIndex + 'application-'.length);

  // Extract until the next space or end of string
  const result = afterApplication.split(' ')[0].trim();

  return result || 'default';
}

const formatTime = (date: Date) => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t('dpo.notifications.time.just_now', 'gerade eben');
  if (diffMins < 60) return t('dpo.notifications.time.minutes_ago', '{{count}} Min. her', { count: diffMins });
  if (diffHours < 24) return t('dpo.notifications.time.hours_ago', '{{count}} Std. her', { count: diffHours });
  if (diffDays < 7) return t('dpo.notifications.time.days_ago', '{{count}} Tag(e) her', { count: diffDays });
  return date.toLocaleDateString('de-DE');
};

const getNotificationIcon = (action: string) => {
  switch (mapActionToNotificationType(action)) {
    case NotificationTypes.success:
      return '✓';
    case NotificationTypes.error:
      return '✕';
    case NotificationTypes.warning:
      return '!';
    default:
      return 'ⓘ';
  }
};

export function NotificationsMenu() {
  const { navigateDashboard, setNavigation } = useNavigation();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { user, activeCompany } = useUserWithCompany();
  const tasks = useTasks();

  // Fetch application table on component mount (but skip if no user)
  const {
    applicationTitleLocalesData,
    loading: applicationTitleLocalesLoading,
    refetch: refetchApplicationTitleLocales,
  } = useApplicationTitleLocalesQuery(!user);
  const [applicationTitleLocales, setApplicationTitleLocales] = useState<{ [key: string]: Application } | null>(null);
  const [queryQuestionnaires, { data: questionnairesData }] = useQuestionnairesQuery();

  // Only show notifications if company is DPO-managed
  const isDpoManaged = activeCompany?.features?.includes('DpoManaged');

  // Fetch notifications continuously (not just when open) so badge count is always accurate
  // Server-side filtering ensures users only receive unseen logs (seen=false)
  const { data: actionLogsData, refetch } = useActionLogsQuery(
    isDpoManaged && !!user
      ? {
          limit: 10000,
          forNotifications: true,
        }
      : undefined
  );

  const actionLogs = useMemo(() => {
    if (!actionLogsData?.actionLogs) {
      return [];
    }

    return [...actionLogsData.actionLogs].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [actionLogsData]);

  const [markActionLogsAsSeen] = useMarkActionLogsAsSeenMutation();

  // Track if we've already done the initial fetch after login
  const initialFetchDoneRef = useRef(false);

  // Only refetch once when user and company become available (right after login)
  useEffect(() => {
    if (isDpoManaged && user && !initialFetchDoneRef.current) {
      initialFetchDoneRef.current = true;
      refetch();
    }
  }, [isDpoManaged, user, refetch]);

  // Set application table data and refetch until we have data
  useEffect(() => {
    setApplicationTitleLocales(applicationTitleLocalesData);
  }, [applicationTitleLocalesLoading]);

  const withdrawApprovalResourceIds = useMemo(() => {
    const resourceIds = actionLogs
      .filter((log) => (log.action === 'dpo_task.withdraw_approval' || log.action === 'dpo_task.approve') && !!log.resourceId)
      .map((log) => log.resourceId);

    return [...new Set(resourceIds)];
  }, [actionLogs]);

  useEffect(() => {
    if (!withdrawApprovalResourceIds.length) {
      return;
    }

    queryQuestionnaires({
      variables: {
        ids: withdrawApprovalResourceIds,
      },
    });
  }, [queryQuestionnaires, withdrawApprovalResourceIds]);

  const withdrawApprovalResourceToApplicationIdMap = useMemo(() => {
    const questionnaires = questionnairesData?.getQuestionnaires || [];

    return questionnaires.reduce(
      (acc, questionnaire) => {
        if (!questionnaire?.id || !questionnaire?.application?.id) {
          return acc;
        }

        acc[questionnaire.id] = questionnaire.application.id;
        return acc;
      },
      {} as Record<string, string>
    );
  }, [questionnairesData]);

  // this maps all tasks by their ID for easy lookup when clicking notifications, to find the correct topicId for navigation
  const taskByIdMap = useMemo(() => {
    return (tasks || []).reduce(
      (acc, task) => {
        if (!task?.id) {
          return acc;
        }

        acc[task.id] = task;
        return acc;
      },
      {} as Record<string, Task>
    );
  }, [tasks]);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Only open menu if application title locales have loaded
    if (applicationTitleLocalesLoading) {
      return;
    }
    setAnchorEl(event.currentTarget);
    // Mark all action logs as seen when opening the dropdown
    // Don't refetch immediately - let the notifications stay visible
    // They'll be filtered out on next page reload or natural refetch
    markActionLogsAsSeen({
      //   variables: { limit: 50 },
    }).catch((err) => console.error('Error marking action logs as seen:', err));
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (actionLogId: string) => {
    const notification = actionLogs.find((al) => al.id === actionLogId);
    if (!notification) {
      handleClose();
      return;
    }

    // Deadline warnings reference a rights request / incident (no application) - go straight to the list
    if (notification.action === ACTION_RIGHTS_DEADLINE_WARNING) {
      navigateDashboard(NavigationSection.Topic, Topics.RIGHTS, NavigationSubsection.Requests);
      handleClose();
      return;
    }
    if (notification.action === ACTION_INCIDENT_DEADLINE_WARNING || notification.action === ACTION_INCIDENT_CREATED) {
      navigateDashboard(NavigationSection.Topic, Topics.DSV, NavigationSubsection.Incidents);
      handleClose();
      return;
    }
    if (notification.action === ACTION_RIGHTS_REQUEST_CREATED) {
      navigateDashboard(NavigationSection.Topic, Topics.RIGHTS, NavigationSubsection.Requests);
      handleClose();
      return;
    }

    const applicationId = getApplicationId(notification);
    const topicId = applicationTitleLocalesData?.[applicationId]?.topicId as Topics | undefined;

    if (
      (notification.action === ACTION_CHAT_MESSAGE_ADD || notification.action === ACTION_TASK_COMPLETE) &&
      applicationId &&
      topicId
    ) {
      const section = notification.resourceId ? taskByIdMap[notification.resourceId]?.section : undefined;
      setNavigation((nav) => {
        nav.pendingNotificationOpen = {
          action: 'open-chat',
          applicationId,
          section,
        };
        nav.questAutoExpandChat = true;
      });
    } else {
      setNavigation((nav) => {
        nav.pendingNotificationOpen = null;
        nav.questAutoExpandChat = false;
      });
    }

    if (topicId) {
      navigateDashboard(NavigationSection.Topic, topicId);
    }

    handleClose();
  };

  // returns the correct application id based on the notification type (actionLog.action)
  //
  // task.reopen:
  //   actionLog.resourceId is EITHER:
  //     - the Task ID, use task context to find topic name
  //     - "company-<id>-application-<id>"", use parser function to find topic
  //
  // task.complete:
  //   actionLog.resourceId is task ID, use task context to find topic name
  //
  // chat.message_add:
  //   actionLog.resourceName is "Chat: DPO Task: company-<id>-application-<id>",
  //   use parser function  to find topic name
  //
  // dpo_task.withdraw_approval:
  //   actionLog.resourceId is questionnaire ID, use questionnaires query to find application ID,
  //   then use application table to find topic name
  function getApplicationId(notification: ActionLog): string {
    switch (notification.action) {
      case ACTION_CHAT_MESSAGE_ADD: {
        return extractApplicationId(notification.resourceName || '');
      }
      case ACTION_TASK_REOPEN: {
        // resourceId can be in two formats, check which one it is in
        // applicationId is in resourceId, extract it with parser function
        if (notification.resourceId.includes('company-')) {
          return extractApplicationId(notification.resourceId);
        }
        // taskId is in resourceId, find topic name via task context
        else {
          const task = notification.resourceId ? taskByIdMap[notification.resourceId] : undefined;
          if (task?.applicationId) {
            return task.applicationId;
          }
          return '';
        }
      }
      case ACTION_TASK_COMPLETE: {
        const task = notification.resourceId ? taskByIdMap[notification.resourceId] : undefined;
        if (task?.applicationId) {
          return task.applicationId;
        }
        return '';
      }
      case ACTION_DPO_TASK_WITHDRAW_APPROVAL:
      case ACTION_DPO_TASK_APPROVE: {
        const questionnaireId = notification.resourceId;
        const applicationId = questionnaireId ? withdrawApprovalResourceToApplicationIdMap[questionnaireId] : undefined;
        if (applicationId) {
          return applicationId;
        }
        return '';
      }

      default: {
        return '';
      }
    }
  }

  function setNotificationChipInfo(notification: ActionLog): string {
    // Deadline warnings carry the item label (e.g. "Auskunftsrecht vom 08.01.2026")
    // as resourceName - there is no application to map
    if (
      notification.action === ACTION_RIGHTS_DEADLINE_WARNING ||
      notification.action === ACTION_INCIDENT_DEADLINE_WARNING ||
      notification.action === ACTION_RIGHTS_REQUEST_CREATED ||
      notification.action === ACTION_INCIDENT_CREATED
    ) {
      return notification.resourceName || '';
    }
    const applicationId = getApplicationId(notification);
    return mapApplicationIdToCorrectLabel(applicationId);
  }

  // maps application IDs to user-friendly labels using the application title locales data
  function mapApplicationIdToCorrectLabel(applicationId: string): string {
    if (applicationTitleLocales && applicationTitleLocales[applicationId]?.titleLocales?.de !== null) {
      return applicationTitleLocales[applicationId]?.titleLocales?.de || '';
    }
    return applicationId;
  }

  const getNotificationActionStyles = (action: string) => {
    if (action === ACTION_CHAT_MESSAGE_ADD) {
      return {
        rowBackgroundColor: 'rgba(112, 16, 185, 0.12)',
        rowHoverBackgroundColor: 'rgba(112, 16, 185, 0.24)',
        rowBorderColor: '#8310b9',
      };
    }

    if (action === ACTION_TASK_REOPEN) {
      return {
        rowBackgroundColor: 'rgba(59, 131, 246, 0.19)',
        rowHoverBackgroundColor: 'rgba(59, 131, 246, 0.34)',
        rowBorderColor: '#3b82f6',
      };
    }

    if (
      action === ACTION_DPO_TASK_WITHDRAW_APPROVAL ||
      action === ACTION_RIGHTS_DEADLINE_WARNING ||
      action === ACTION_INCIDENT_DEADLINE_WARNING
    ) {
      return {
        rowBackgroundColor: 'rgba(245, 159, 11, 0.11)',
        rowHoverBackgroundColor: 'rgba(245, 159, 11, 0.25)',
        rowBorderColor: '#f59e0b',
      };
    }

    if (action === ACTION_DPO_TASK_APPROVE) {
      return {
        rowBackgroundColor: 'rgba(16, 185, 129, 0.12)',
        rowHoverBackgroundColor: 'rgba(16, 185, 129, 0.24)',
        rowBorderColor: '#10b981',
      };
    }

    return {
      rowBackgroundColor: 'rgba(75, 246, 59, 0.12)',
      rowHoverBackgroundColor: 'rgba(59, 246, 59, 0.23)',
      rowBorderColor: '#54f63b',
    };
  };

  // Don't render notification bell if company is not DPO-managed
  if (!isDpoManaged) {
    return null;
  }

  return (
    <>
      <Tooltip
        enterTouchDelay={0}
        leaveTouchDelay={5000}
        title={t('dpo.notifications.notifications', 'Benachrichtigungen')}
      >
        <button
          className="dtHeader__button relative"
          onClick={handleClick}
          aria-label="notifications"
          data-testid="notifications-button"
          disabled={applicationTitleLocalesLoading}
        >
          <Badge
            badgeContent={actionLogs.length}
            color="error"
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: '#dc2626',
                color: '#fff',
                fontSize: '0.65rem',
                minWidth: '18px',
                height: '18px',
              },
            }}
          >
            <NotificationsNoneIcon />
          </Badge>
        </button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '8px',
              overflow: 'hidden',
              width: '650px',
              maxWidth: '90vw',
              maxHeight: '90vh',
              mt: 1,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t('dpo.notifications.notifications', 'Benachrichtigungen')}
          </Typography>
          {/* Notifications are automatically marked as seen when dropdown opens */}
        </Box>
        <Divider />

        {/* Notifications List */}
        {actionLogs.length === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <Typography color="textSecondary" variant="body2">
              {t('dpo.notifications.no_messages', 'Keine Benachrichtigungen')}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              maxHeight: 'calc(90vh - 80px)',
              overflowY: 'auto',
              overflowX: 'hidden',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#555',
              },
            }}
          >
            {actionLogs.map((notification, index) => (
              <div key={notification.id}>
                {(() => {
                  const chipLabel = setNotificationChipInfo(notification);
                  const actionStyles = getNotificationActionStyles(notification.action);
                  const notifType = mapActionToNotificationType(notification.action);
                  return (
                    <MenuItem
                      onClick={() => handleNotificationClick(notification.id)}
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        bgcolor: !notification.seen ? actionStyles.rowBackgroundColor : 'transparent',
                        borderLeft: !notification.seen
                          ? `3px solid ${actionStyles.rowBorderColor}`
                          : '3px solid transparent',
                        '&:hover': {
                          bgcolor: !notification.seen ? actionStyles.rowHoverBackgroundColor : 'rgba(0, 0, 0, 0.04)',
                        },
                        transition: 'all 0.2s ease',
                        minHeight: 'auto',
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1, width: '100%', alignItems: 'center' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            flexShrink: 0,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            backgroundColor:
                              notifType === NotificationTypes.success
                                ? '#d1fae5'
                                : notifType === NotificationTypes.error
                                  ? '#fee2e2'
                                  : notifType === NotificationTypes.warning
                                    ? '#fef3c7'
                                    : '#dbeafe',
                            color:
                              notifType === NotificationTypes.success
                                ? '#065f46'
                                : notifType === NotificationTypes.error
                                  ? '#991b1b'
                                  : notifType === NotificationTypes.warning
                                    ? '#92400e'
                                    : '#1e40af',
                          }}
                        >
                          {getNotificationIcon(notification.action)}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: !notification.seen ? 600 : 500,
                              mb: 0.15,
                              fontSize: '0.875rem',
                            }}
                          >
                            {mapActionToNotificationTitle(notification.action)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, minWidth: 0 }}>
                            {chipLabel && (
                              <Chip
                                label={chipLabel}
                                size="small"
                                sx={{
                                  height: '16px',
                                  fontSize: '0.625rem',
                                  fontWeight: 500,
                                  color: 'textSecondary',
                                  bgcolor: 'rgba(0, 0, 0, 0.05)',
                                  maxWidth: '480px',
                                  '& .MuiChip-label': {
                                    px: 0.75,
                                    py: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  },
                                }}
                              />
                            )}
                            <Typography
                              variant="caption"
                              sx={{ color: 'textDisabled', fontSize: '0.65rem', ml: 'auto' }}
                            >
                              {formatTime(new Date(notification.createdAt))}
                            </Typography>
                          </Box>
                        </Box>
                        {!notification.seen && (
                          <Box
                            sx={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  );
                })()}
                {index < actionLogs.length - 1 && <Divider sx={{ my: 0 }} />}
              </div>
            ))}
          </Box>
        )}
      </Menu>
    </>
  );
}
